import { StripePermissions } from '@/types/Organization';
import { evaluatePolicy } from './policyEvaluators';
import {
  Action,
  OrganizationPolicyData,
  OrganizationResourceData,
  WorkspacePolicyData,
  WorkspaceResourceData,
} from './types';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { isUndefined, some } from 'lodash';
import { DEFAULT_MISSING_PERMISSION_MESSAGE } from './consts';

export function hasPermission<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
  R extends keyof T,
  A extends keyof T[R],
>(
  policyLevel: L,
  resource: R,
  action: A,
  ...args: T[R][A] extends never ? [] : [res: T[R][A]]
): boolean {
  const resourceData = args[0];
  const data = getData(policyLevel);
  if (!hasAllData(data, policyLevel)) {
    console.error('Found missing data while trying to perform permissions check');
    return false;
  }
  const [policyData, constraints] = data;

  const res = resourceData
    ? evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints,
        resourceData,
      })
    : evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints,
      });

  return processDefaultResult(res);
}

export function hasPermissionWithPolicy<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
  R extends keyof T,
  A extends keyof T[R],
>(
  policyData: L extends 'workspace' ? WorkspacePolicyData : OrganizationPolicyData,
  constraints: StripePermissions | undefined,
  policyLevel: L,
  resource: R,
  action: A,
  ...args: T[R][A] extends never ? [] : [res: T[R][A]]
): boolean {
  const resourceData = args[0];
  const data: [
    L extends 'workspace' ? WorkspacePolicyData : OrganizationPolicyData,
    StripePermissions | undefined,
  ] = [policyData, constraints];

  if (!hasAllData(data, policyLevel)) {
    console.error('Found missing data while trying to perform permissions check');
    return false;
  }

  const [, constrs] = data;

  const res = resourceData
    ? evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints: constrs,
        resourceData,
      })
    : evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints: constrs,
      });

  return processDefaultResult(res);
}

export function hasPermissionMessage<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
  R extends keyof T,
  A extends keyof T[R],
>(
  policyLevel: L,
  resource: R,
  action: A,
  ...args: T[R][A] extends never ? [] : [res: T[R][A]]
): readonly [boolean, string | null] {
  const resourceData = args[0];
  const data = getData(policyLevel);

  if (!hasAllData(data, policyLevel)) {
    console.error('Found missing data while trying to perform permissions check');
    return [false, 'Found missing data while trying to perform permissions check'];
  }
  const [policyData, constraints] = data;

  const res = resourceData
    ? evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints,
        resourceData,
      })
    : evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints,
      });

  return processMessagerResult(res);
}

export function hasPermissionMessageWithPolicy<
  L extends 'workspace' | 'organization',
  T extends L extends 'workspace' ? WorkspaceResourceData : OrganizationResourceData,
  R extends keyof T,
  A extends keyof T[R],
>(
  policyData: L extends 'workspace' ? WorkspacePolicyData : OrganizationPolicyData,
  constraints: StripePermissions | undefined,
  policyLevel: L,
  resource: R,
  action: A,
  ...args: T[R][A] extends never ? [] : [res: T[R][A]]
): readonly [boolean, string | null] {
  const resourceData = args[0];
  const data: [
    L extends 'workspace' ? WorkspacePolicyData : OrganizationPolicyData,
    StripePermissions | undefined,
  ] = [policyData, constraints];

  if (!hasAllData(data, policyLevel)) {
    console.error('Found missing data while trying to perform permissions check');
    return [false, 'Found missing data while trying to perform permissions check'];
  }

  const [, constrs] = data;

  const res = resourceData
    ? evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints: constrs,
        resourceData,
      })
    : evaluatePolicy({
        action: action as Action,
        policyLevel,
        resource: resource as
          | keyof WorkspaceResourceData
          | keyof OrganizationResourceData,
        policyData,
        constraints: constrs,
      });

  return processMessagerResult(res);
}

function processDefaultResult([isRoleAllowingAction, isPolicyAllowingAction]: [
  boolean,
  boolean | string,
]): boolean {
  const castedIsPolicyAllowingAction =
    typeof isPolicyAllowingAction === 'string' ? false : isPolicyAllowingAction;

  return isRoleAllowingAction && castedIsPolicyAllowingAction;
}

function processMessagerResult([isRoleAllowingAction, isPolicyAllowingAction]: [
  boolean,
  boolean | string,
]): readonly [boolean, string | null] {
  const castedIsPolicyAllowingAction =
    typeof isPolicyAllowingAction === 'string' ? false : isPolicyAllowingAction;
  const isAccessGranted = isRoleAllowingAction && castedIsPolicyAllowingAction;
  const message = isAccessGranted
    ? null
    : typeof isPolicyAllowingAction === 'string'
    ? isPolicyAllowingAction
    : DEFAULT_MISSING_PERMISSION_MESSAGE;

  return [isAccessGranted, message] as const;
}

function getData<L extends 'organization' | 'workspace'>(
  policyLevel: L,
): [
  L extends 'organization'
    ? Partial<OrganizationPolicyData>
    : Partial<WorkspacePolicyData>,
  StripePermissions | undefined,
] {
  const searchParams = new URLSearchParams(window.location.search);
  const workspaceId = searchParams.get('workspaceId');
  const selectedOrganization = useOrganizationStore.getState().selectedOrganization;
  const permissions = useUserPermissionsStore.getState();
  const selectedWorkspace = selectedOrganization?.workspaces.find(
    (ws) => ws.id === workspaceId,
  );
  const organizationRole = permissions.organizationPermission;
  const workspaceRole = permissions.workspacePermission;
  const policyData =
    policyLevel === 'organization'
      ? {
          organizationRole: organizationRole?.role,
          selectedOrganization: selectedOrganization || undefined,
        }
      : {
          selectedWorkspace: selectedWorkspace,
          workspaceRole: workspaceRole?.role,
        };
  const constraints: StripePermissions | undefined = selectedOrganization?.permissions;
  return [policyData, constraints];
}

function hasAllData<L extends 'organization' | 'workspace'>(
  args: ReturnType<typeof getData>,
  policyLevel: L,
): args is [
  L extends 'workspace' ? WorkspacePolicyData : OrganizationPolicyData,
  StripePermissions,
] {
  const [policyData, constraints] = [...args];
  if (policyLevel === 'organization') {
    const isNotOrganizationMember =
      typeof (policyData as OrganizationPolicyData).selectedOrganization
        ?.isOrganizationMember === 'boolean' &&
      !(policyData as OrganizationPolicyData).selectedOrganization.isOrganizationMember;
    if (isNotOrganizationMember)
      delete (policyData as OrganizationPolicyData).organizationRole;
  }
  if (!constraints) return false;

  const isAnyUndefined = some({ ...policyData, ...constraints }, isUndefined);

  return !isAnyUndefined;
}
